import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Tabella } from '../../componenti/base.tsx'
import { giorno, messaggioErrore } from '../../componenti/elenco.tsx'

/**
 * «Unisci con un'altra scheda» (473; Osservazioni n. 4 del 23/10/2026). Dalla scheda da tenere si
 * cerca l'altra (nome, numero di scheda o codice dell'archivio precedente), si legge l'anteprima e
 * si conferma: percorsi, interventi, consensi, documenti e ogni altro dato passano a questa scheda,
 * i suoi campi vuoti si riempiono con quelli dell'altra, che resta come rimando. Le decisioni e i
 * testi sono dell'API (rotte/unisci-schede.ts): qui si mostrano.
 */

type SchedaBreve = {
  id: string; numero_scheda: number; etichetta: string; codice_storico: string | null
  data_nascita: string | null; sede: string | null; stato: string
}
type Conteggio = { tabella: string; colonna: string; nome: string; righe: number; passano: number; tolte: number; nota: string | null }
type Campo = { campo: string; nome: string; esito: 'riempito' | 'diverso' | 'entrambi' | 'aggiunto'; tenuta: string | null; unita: string | null }
export type Anteprima = {
  tenuta: SchedaBreve; unita: SchedaBreve; conteggi: Conteggio[]; totale: number; campi: Campo[]
  doppioni: string[]; ostacoli: string[]; avvisi: string[]; impronta: string
}
export type SchedaSimile = {
  id: string; numero_scheda: number; etichetta: string; data_nascita: string | null; precisione_nascita: string
  codice_storico: string | null; motivo: 'stesso_codice_fiscale' | 'nome_simile'
}
type Trovata = { id: string; numero_scheda: number; etichetta: string; data_nascita?: string | null; mascherata: boolean }

const ESITI: Record<Campo['esito'], string> = {
  riempito: 'questa scheda non lo ha: prende il valore dell’altra',
  diverso: 'resta quello di questa scheda',
  entrambi: 'c’è in tutte e due: resta quello di questa scheda',
  aggiunto: 'le note dell’altra si aggiungono in coda a queste',
}
const quante = (n: number, una: string, tante: string) => (n === 1 ? `1 ${una}` : `${n} ${tante}`)
const nascita = (d: string | null) => (d ? ` · nascita ${d}` : '')

/** Le schede molto simili a questa, per chi puo' unirle: un avviso con il comando per ciascuna. */
export function AvvisoSchedeSimili({ personaId, unisci }: { personaId: string; unisci: (id: string) => void }) {
  const [simili, setSimili] = useState<SchedaSimile[]>([])
  useEffect(() => {
    let vivo = true
    api.get<SchedaSimile[]>(`/persone/${personaId}/simili`).then((r) => { if (vivo) setSimili(r) })
      .catch(() => { if (vivo) setSimili([]) })
    return () => { vivo = false }
  }, [personaId])
  if (!simili.length) return null
  return (
    <Avviso tipo="attenzione">
      <strong>{simili.length === 1 ? 'C’è una scheda molto simile a questa.' : 'Ci sono schede molto simili a questa.'}</strong>
      {' '}Se sono la stessa persona, confrontale e uniscile: tutto passa a questa scheda.
      <ul className="elenco-avviso">
        {simili.map((s) => (
          <li key={s.id} className="riga">
            <span>
              <Link to={`/persone/${s.id}`}>scheda n. {s.numero_scheda} — {s.etichetta}</Link>
              <span className="tenue">
                {s.data_nascita ? ` · nascita ${giorno(s.data_nascita)}` : ''}
                {s.codice_storico ? ` · archivio precedente ${s.codice_storico}` : ''}
                {s.motivo === 'stesso_codice_fiscale' ? ' · stesso codice fiscale' : ' · nome quasi uguale'}
              </span>
            </span>
            <span className="spinta">
              <Bottone variante="secondario" dimensione="piccolo" onClick={() => unisci(s.id)}>Confronta e unisci</Bottone>
            </span>
          </li>
        ))}
      </ul>
    </Avviso>
  )
}

export function UnisciSchede({ scheda, proposta, chiudi, fatto }: {
  scheda: { id: string; numero_scheda: number; etichetta: string }
  /** Scheda da unire gia' scelta (dall'avviso delle schede simili o dalla pagina Migrazione). */
  proposta?: string | null
  chiudi: () => void
  fatto: (testo: string) => void
}) {
  const [cerca, setCerca] = useState('')
  const [trovate, setTrovate] = useState<Trovata[]>([])
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function controlla(unita: string) {
    setErrore(null); setInCorso(true)
    try {
      setAnteprima(await api.post<Anteprima>(`/persone/${scheda.id}/unisci/anteprima`, { unita }))
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  useEffect(() => { if (proposta) void controlla(proposta) }, [proposta]) // eslint-disable-line react-hooks/exhaustive-deps

  // Ricerca dell'altra scheda: nome, numero di scheda o codice dell'archivio precedente, come
  // nell'elenco Beneficiari (il testo viaggia nel corpo, non nell'indirizzo).
  useEffect(() => {
    const testo = cerca.trim()
    if (testo.length < 2) { setTrovate([]); return }
    let vivo = true
    const t = setTimeout(() => {
      api.post<{ risultati: Trovata[] }>('/persone/cerca', { cerca: testo, limite: 10 })
        .then((r) => { if (vivo) setTrovate(r.risultati.filter((x) => x.id !== scheda.id)) })
        .catch(() => { if (vivo) setTrovate([]) })
    }, 250)
    return () => { vivo = false; clearTimeout(t) }
  }, [cerca, scheda.id])

  async function unisci() {
    if (!anteprima) return
    setErrore(null); setInCorso(true)
    try {
      await api.post(`/persone/${scheda.id}/unisci`, { unita: anteprima.unita.id, impronta: anteprima.impronta })
      fatto(`La scheda n. ${anteprima.unita.numero_scheda} è stata unita a questa: tutto quello che aveva ora è qui.`)
    } catch (e) {
      setErrore(messaggioErrore(e))
      if (e instanceof ErroreApi && e.codice === 'ANTEPRIMA_CAMBIATA') void controlla(anteprima.unita.id)
    } finally {
      setInCorso(false)
    }
  }

  if (anteprima) {
    const { tenuta, unita } = anteprima
    const passano = anteprima.conteggi.filter((c) => c.passano > 0)
    const restano = anteprima.conteggi.filter((c) => c.passano === 0)
    return (
      <Modale larga titolo={`Unire la scheda n. ${unita.numero_scheda} a questa?`} chiudi={chiudi}
              fuocoIniziale="azioni"
              azioni={<>
                <Bottone variante="discreto" onClick={() => { setAnteprima(null); setErrore(null) }}>Torna alla scelta</Bottone>
                <Link className="bottone bottone-discreto" to={`/persone/${unita.id}?unisci=${tenuta.id}`} onClick={chiudi}>
                  Tieni invece la n. {unita.numero_scheda}
                </Link>
                <Bottone variante="critico" inCorso={inCorso} disabled={anteprima.ostacoli.length > 0}
                         onClick={() => void unisci()}>
                  Unisci le schede
                </Bottone>
              </>}>
        <dl className="persona-intestazione">
          <div className="persona-intestazione-voce persona-intestazione-larga">
            <dt>Si tiene</dt>
            <dd>
              <strong>scheda n. {tenuta.numero_scheda} — {tenuta.etichetta}</strong>
              <span className="tenue">{nascita(tenuta.data_nascita)}{tenuta.sede ? ` · ${tenuta.sede}` : ''}
                {tenuta.codice_storico ? ` · archivio precedente ${tenuta.codice_storico}` : ''}</span>
            </dd>
          </div>
          <div className="persona-intestazione-voce persona-intestazione-larga">
            <dt>Si unisce</dt>
            <dd>
              <strong>scheda n. {unita.numero_scheda} — {unita.etichetta}</strong>
              <span className="tenue">{nascita(unita.data_nascita)}{unita.sede ? ` · ${unita.sede}` : ''}
                {unita.codice_storico ? ` · archivio precedente ${unita.codice_storico}` : ''}</span>
            </dd>
          </div>
        </dl>

        {anteprima.ostacoli.length > 0 && (
          <Avviso tipo="errore">
            <strong>Le schede non si possono unire.</strong>
            <ul className="elenco-avviso">{anteprima.ostacoli.map((o) => <li key={o}>{o}</li>)}</ul>
          </Avviso>
        )}

        <h3>Che cosa passa a questa scheda</h3>
        {passano.length
          ? (
            <ul className="elenco-avviso">
              {passano.map((c) => (
                <li key={`${c.tabella}.${c.colonna}`}>
                  {c.nome}: {c.passano}
                  {c.tolte > 0 && <span className="tenue"> ({quante(c.tolte, 'già presente qui non passa', 'già presenti qui non passano')})</span>}
                  {c.nota && <span className="tenue"> · {c.nota}</span>}
                </li>
              ))}
            </ul>
          )
          : <p className="tenue">L’altra scheda non ha percorsi, interventi, documenti o altri dati collegati.</p>}
        {restano.length > 0 && (
          <p className="piccolo tenue">
            Non passano: {restano.map((c) => `${c.nome} (${c.righe}${c.nota ? `, ${c.nota}` : ''})`).join('; ')}.
          </p>
        )}

        <h3>Dati anagrafici</h3>
        {anteprima.campi.length
          ? (
            <Tabella intestazioni={['Campo', 'Questa scheda', 'L’altra scheda', 'Che cosa succede']}>
              {anteprima.campi.map((c) => (
                <tr key={c.campo}>
                  <td data-etichetta="Campo">{c.nome}</td>
                  <td data-etichetta="Questa scheda">{c.tenuta ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="L’altra scheda">{c.unita ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="Che cosa succede">
                    <Etichetta tono={c.esito === 'riempito' || c.esito === 'aggiunto' ? 'informativo' : 'neutro'}>
                      {ESITI[c.esito]}
                    </Etichetta>
                  </td>
                </tr>
              ))}
            </Tabella>
          )
          : <p className="tenue">I dati anagrafici coincidono, oppure l’altra scheda non ne ha in più.</p>}
        <p className="piccolo tenue">
          Codice fiscale, contatti, indirizzi e note non si aprono per l’anteprima: si dice solo se ci sono.
          I dati di questa scheda restano; quelli che mancano si prendono dall’altra.
        </p>

        {anteprima.doppioni.length > 0 && (
          <>
            <h3>Dati presenti in tutte e due</h3>
            <ul className="elenco-avviso">{anteprima.doppioni.map((d) => <li key={d}>{d}</li>)}</ul>
          </>
        )}
        {anteprima.avvisi.length > 0 && (
          <Avviso>
            <ul className="elenco-avviso">{anteprima.avvisi.map((a) => <li key={a}>{a}</li>)}</ul>
          </Avviso>
        )}
        <p className="piccolo tenue">
          L’altra scheda non si cancella: resta come rimando a questa, non compare più negli elenchi e chi la apre
          arriva qui. L’unione non si annulla dalla pagina; i valori di prima restano registrati.
        </p>
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
      </Modale>
    )
  }

  return (
    <Modale larga titolo="Unisci con un’altra scheda" chiudi={chiudi}
            azioni={<Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>}>
      <p className="piccolo">
        Si tiene <strong>questa scheda</strong> (n. {scheda.numero_scheda}, {scheda.etichetta}): cerca l’altra scheda della
        stessa persona. Prima di unire vedrai che cosa passa e quali dati cambiano.
      </p>
      <Campo tipo="search" etichetta="Scheda da unire" valore={cerca} cambia={setCerca} maxLength={120}
             aiuto="Nome e cognome (anche invertiti), numero di scheda o codice dell’archivio precedente." />
      {trovate.length > 0 && (
        <ul className="mediazioni-elenco-unione">
          {trovate.map((t) => (
            <li key={t.id} className="riga">
              <span>
                scheda n. {t.numero_scheda} — {t.etichetta}
                {t.data_nascita && <span className="tenue"> · nascita {giorno(t.data_nascita)}</span>}
              </span>
              <span className="spinta">
                <Bottone variante="secondario" dimensione="piccolo" inCorso={inCorso} disabled={t.mascherata}
                         onClick={() => void controlla(t.id)}>
                  Controlla l’unione
                </Bottone>
              </span>
            </li>
          ))}
        </ul>
      )}
      {cerca.trim().length >= 2 && !trovate.length && <p className="tenue">Nessuna scheda trovata.</p>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
